import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 25,
    fontFamily: 'Helvetica',
    backgroundColor: '#FFFFFF',
    color: '#1F2937',
    lineHeight: 1.3,
    fontSize: 9,
  },
  header: {
    marginBottom: 15,
    paddingBottom: 10,
    borderBottom: '1px solid #E5E7EB',
  },
  title: {
    fontSize: 20,
    fontWeight: 'extrabold',
    marginBottom: 4,
    color: '#111827',
  },
  subtitle: {
    fontSize: 12,
    color: '#4B5563',
    fontStyle: 'italic',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#111827',
    borderBottom: '1px solid #E5E7EB',
    paddingBottom: 4,
  },
  listItem: {
    marginBottom: 5,
    marginLeft: 8,
    fontSize: 9,
    textAlign: 'justify',
    width: '100%',
  },
  subSection: {
    marginLeft: 5,
    marginBottom: 12,
  },
  subSectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginTop: 8,
    marginBottom: 6,
    color: '#1F2937',
  },
  highlight: {
    backgroundColor: '#F9FAFB',
    padding: 10,
    borderRadius: 4,
    borderLeft: '3px solid #3B82F6',
    marginBottom: 12,
  },
  reference: {
    fontSize: 8,
    color: '#6B7280',
    fontStyle: 'italic',
    marginTop: 2,
    marginBottom: 8,
  },
  table: {
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 0.5,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginVertical: 8,
  },
  tableRow: {
    flexDirection: 'row',
    minHeight: 20,
  },
  tableCol: {
    width: '33.33%',
    borderStyle: 'solid',
    borderWidth: 0.5,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 3,
    fontSize: 8,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'left',
    wordWrap: 'break-word',
  },
  tableHeader: {
    backgroundColor: '#F3F4F6',
    fontWeight: 'bold',
  },
  footer: {
    position: 'absolute',
    bottom: 15,
    left: 25,
    right: 25,
    textAlign: 'center',
    fontSize: 7,
    color: '#9CA3AF',
    borderTop: '0.5px solid #E5E7EB',
    paddingTop: 5,
  },
});

const CreditScoreGuide = () => (
  <Document>
    <Page size="A4" style={styles.page} wrap>
      <View style={{ flex: 1 }}>
        <View style={styles.header}>
          <Text style={styles.title}> Credit Score Improvement Guide</Text>
          <Text style={styles.subtitle}>Your comprehensive guide to boosting your credit score</Text>
        </View>

        <View style={styles.section}>
          <View style={styles.subSection}>
            <Text style={styles.sectionTitle}>1. Understand Your CIBIL Score</Text>
            <Text style={styles.listItem}>• Scale: Ranges from 300–900; 300–549 is poor, 550–649 is fair, 650–749 good, and 750+ excellent</Text>
            <Text style={styles.listItem}>• Why it matters: Above 700–750 improves approval chances and loan terms</Text>
            <Text style={styles.reference}>References: bajajfinserv.in, paisabazaar.com</Text>
          </View>

          <View style={styles.subSection}>
            <Text style={styles.sectionTitle}>✅ Step-by-Step Improvement Plan</Text>

            <Text style={styles.subSectionTitle}>2. Check & Dispute Mistakes</Text>
            <Text style={styles.listItem}>• Pull your CIBIL report quarterly or annually—soft checks won't affect your score</Text>
            <Text style={styles.reference}>References: marketfeed.com, paisabazaar.com, hsbc.co.in</Text>
            <Text style={styles.listItem}>• Spot inaccuracies (e.g., incorrect late payments) and raise disputes; resolution takes ~30 days</Text>

            <Text style={styles.subSectionTitle}>3. Pay Dues on Time Always</Text>
            <Text style={styles.listItem}>• Payment history is ~35% of score—set reminders or auto-debit for EMIs and credit cards</Text>
            <Text style={styles.reference}>References: blog.bankbazaar.com, hdfcbank.com, zetapp.in</Text>
            <Text style={styles.listItem}>• Timely payments build trust and gradually improve scores</Text>

            <Text style={styles.subSectionTitle}>4. Keep Utilization Below 30%</Text>
            <Text style={styles.listItem}>• Don't max out credit cards—maintain usage under 30% of limit</Text>
            <Text style={styles.reference}>References: zetapp.in, sc.com</Text>
            <Text style={styles.listItem}>• Paying twice a month helps lower reported balances</Text>

            <Text style={styles.subSectionTitle}>5. Limit New Credit & Hard Inquiries</Text>
            <Text style={styles.listItem}>• Each loan or card application triggers a 'hard inquiry' that lowers score slightly</Text>
            <Text style={styles.reference}>References: livemint.com, paisabazaar.com, marketfeed.com</Text>
            <Text style={styles.listItem}>• Apply only when needed; shop rates via soft queries instead</Text>
            <Text style={styles.reference}>References: researchgate.net</Text>

            <Text style={styles.subSectionTitle}>6. Maintain Old Accounts</Text>
            <Text style={styles.listItem}>• Longer credit history (~15% of score) helps your profile</Text>
            <Text style={styles.reference}>References: marketfeed.com</Text>
            <Text style={styles.listItem}>• Keep old, fee-free accounts open</Text>

            <Text style={styles.subSectionTitle}>7. Build a Credit Mix</Text>
            <Text style={styles.listItem}>• Have both secured (home, auto) and unsecured (cards, personal loans) credit types—10% of score</Text>
            <Text style={styles.reference}>References: hdfcbank.com, moneyview.in</Text>

            <Text style={styles.subSectionTitle}>8. Clear Past-Due Amounts</Text>
            <Text style={styles.listItem}>• Pay off any overdue or defaulted loans</Text>
            <Text style={styles.listItem}>• Negotiate part-pay or settlements if needed</Text>

            <Text style={styles.subSectionTitle}>9. Request Higher Credit Limits</Text>
            <Text style={styles.listItem}>• A higher limit, with stable usage, lowers utilization ratio</Text>
            <Text style={styles.reference}>References: zetapp.in, sc.com, hsbc.co.in</Text>

            <Text style={styles.subSectionTitle}>10. Monitor Regularly</Text>
            <Text style={styles.listItem}>• Monitor monthly or bi-monthly; active checking improves score by ~22 bps annually</Text>
            <Text style={styles.reference}>References: m.economictimes.com, livemint.com, timesofindia.indiatimes.com</Text>
            <Text style={styles.listItem}>• Subscribe to MyCIBIL, or use apps like CRED or Paytm for alerts</Text>
          </View>

          <View style={[styles.subSection, styles.highlight]}>
            <Text style={styles.sectionTitle}>Real Case Studies</Text>
            <Text style={styles.listItem}>• Rahul (Score 637): Repaid debts, improved utilization and income stability—qualified for a Rs 1.26 lakh loan</Text>
            <Text style={styles.reference}>References: omozing.com</Text>
            <Text style={styles.listItem}>• Prince (Score ~750): Kept home loan EMIs paid on time over 2 years—score rose to 750</Text>
            <Text style={styles.reference}>References: blog.bankbazaar.com, paisabazaar.com</Text>
            <Text style={styles.listItem}>• Dr. Bhalla: Bank error wrongly flagged dues—legal fight reclaimed score & caused policy change</Text>
            <Text style={styles.reference}>References: m.economictimes.com</Text>
          </View>

          <View style={styles.subSection}>
            <Text style={styles.sectionTitle}>Summary Table</Text>
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={styles.tableCol}>Action</Text>
                <Text style={styles.tableCol}>Helps With</Text>
                <Text style={styles.tableCol}>Time Frame</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCol}>Check & dispute/repair errors</Text>
                <Text style={styles.tableCol}>Correcting score-impacting mistakes</Text>
                <Text style={styles.tableCol}>1–2 months</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCol}>Pay bills & EMIs on time</Text>
                <Text style={styles.tableCol}>Payment history (35%)</Text>
                <Text style={styles.tableCol}>Ongoing</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCol}>Keep utilization &lt; 30%</Text>
                <Text style={styles.tableCol}>Utilization ratio (≈30%)</Text>
                <Text style={styles.tableCol}>1–3 months</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCol}>Avoid multiple inquiries</Text>
                <Text style={styles.tableCol}>Credit pulls</Text>
                <Text style={styles.tableCol}>Ongoing</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCol}>Keep old accounts open</Text>
                <Text style={styles.tableCol}>Credit history length (15%)</Text>
                <Text style={styles.tableCol}>Long-term</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCol}>Diversify credit</Text>
                <Text style={styles.tableCol}>Credit mix (10%)</Text>
                <Text style={styles.tableCol}>Ongoing</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCol}>Clear past-due debts</Text>
                <Text style={styles.tableCol}>Removing negative marks</Text>
                <Text style={styles.tableCol}>3–6 months</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCol}>Increase credit limits</Text>
                <Text style={styles.tableCol}>Improves utilization ratio</Text>
                <Text style={styles.tableCol}>1–2 months</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCol}>Monitor 30–45 days</Text>
                <Text style={styles.tableCol}>Track progress & errors</Text>
                <Text style={styles.tableCol}>Ongoing</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <Text>© {new Date().getFullYear()} White Pearl Finance - Confidential - For Client Use Only</Text>
        </View>
      </View>
    </Page>
  </Document>
);

export default CreditScoreGuide;
